import { useState, useCallback } from 'react';
import { ZodError, ZodTypeAny, ZodRawShape, ZodObject } from 'zod';

interface ValidationErrors {
  [key: string]: string;
}

interface UseFormValidationReturn<T> {
  errors: ValidationErrors;
  validateField: (name: keyof T, value: any) => boolean;
  validateForm: (data: T) => boolean;
  clearError: (name: keyof T) => void;
  clearAllErrors: () => void;
  setError: (name: keyof T, message: string) => void;
}

// Add ZodSchema type that is assignable to schema as object
type AnyZodObject = ZodObject<ZodRawShape, any>;

export function useFormValidation<T extends Record<string, any>>(
  schema: AnyZodObject
): UseFormValidationReturn<T> {
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Validate a single field
  const validateField = useCallback(
    (name: keyof T, value: any): boolean => {
      try {
        // Pick the single field's schema from the object
        const fieldSchema = schema.shape[name as string] as ZodTypeAny;
        if (!fieldSchema) {
          // If schema for field doesn't exist, don't validate - just pass.
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[name as string];
            return newErrors;
          });
          return true;
        }
        fieldSchema.parse(value);

        // Clear error if validation passes
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name as string];
          return newErrors;
        });

        return true;
      } catch (error) {
        if (error instanceof ZodError) {
          // errors is an array
          const fieldError = error.issues?.[0];
          setErrors((prev) => ({
            ...prev,
            [name as string]: fieldError?.message || 'Invalid value',
          }));
        }
        return false;
      }
    },
    [schema]
  );

  // Validate entire form
  const validateForm = useCallback(
    (data: T): boolean => {
      try {
        schema.parse(data);
        setErrors({});
        return true;
      } catch (error) {
        if (error instanceof ZodError) {
          const newErrors: ValidationErrors = {};
          if (Array.isArray(error.issues)) {
            error.issues.forEach((err) => {
              if (err.path && err.path[0]) {
                newErrors[err.path[0] as string] = err.message;
              }
            });
          }
          setErrors(newErrors);
        } else {
          console.error('Validation error:', error);
        }
        return false;
      }
    },
    [schema]
  );

  // Clear error for a specific field
  const clearError = useCallback((name: keyof T) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name as string];
      return newErrors;
    });
  }, []);

  // Clear all errors
  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Set a custom error (useful for server-side errors)
  const setError = useCallback((name: keyof T, message: string) => {
    setErrors((prev) => ({
      ...prev,
      [name as string]: message,
    }));
  }, []);

  return {
    errors,
    validateField,
    validateForm,
    clearError,
    clearAllErrors,
    setError,
  };
}
